import React from "react";
import { Link } from "react-router";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import type { IncidentVm } from "@/features/daily/components/command/command-view-model";
import { HealthCauses } from "@/features/daily/components/command/HealthCauses";
import type { HealthCausesVm, IncidentRegisterVm } from "@/features/daily/components/command/health-view-model";
import { IncidentsPanel } from "@/features/daily/components/incidents/IncidentsPanel";

type Props = {
    register: IncidentRegisterVm;
    incidents: IncidentVm[];
    viewerId: string;
    calmNote: string;
    queueHref: string;
    onApplied: () => void;
    /** B10: the live causes, shown here when the persistent register is not provisioned. */
    causes: HealthCausesVm | null;
};

const countLabel = (register: IncidentRegisterVm): string => {
    if (register.status === "unavailable") return "Registro no disponible";
    if (register.openCount === 1) return "1 incidencia";
    return `${register.openCount} incidencias`;
};

/**
 * FR-010/FR-011: what needs a person now. When the incident register itself is not
 * provisioned (`index_missing`) the panel says so — an empty list would otherwise
 * read as "nothing is wrong", which nobody measured.
 */
export const AttentionPanel: React.FC<Props> = ({ register, incidents, viewerId, calmNote, queueHref, onApplied, causes }) => (
    <section className="daily-panel daily-attention" aria-labelledby="daily-attention-title">
        <div className="daily-panel__head">
            <h2 id="daily-attention-title">Necesita atención</h2>
            <span className="daily-pill">{countLabel(register)}</span>
        </div>
        {register.status === "unavailable" ? (
            causes === null ? (
                <div className="daily-empty" data-tone="neutral" role="status">
                    <DailyIcon name="help" />
                    <div>
                        <p>{register.title}</p>
                        <small>{register.body}</small>
                    </div>
                </div>
            ) : (
                // D117: without the persistent register, the live causes still say what needs a person.
                <>
                    {causes.affecting.length === 0 && causes.warnings.length === 0 ? (
                        <div className="daily-empty">
                            <DailyIcon name="check_circle" />
                            <div>
                                <p>Nada fuera de objetivo en las comprobaciones en vivo</p>
                                <small>{calmNote}</small>
                            </div>
                        </div>
                    ) : null}
                    <HealthCauses causes={causes} />
                    <p className="daily-small daily-muted">{register.title}: las causas se calculan en vivo en cada consulta.</p>
                </>
            )
        ) : (
            <IncidentsPanel
                incidents={incidents}
                viewerId={viewerId}
                onApplied={onApplied}
                empty={(
                    <div className="daily-empty">
                        <DailyIcon name="check_circle" />
                        <div>
                            <p>No hay incidencias activas</p>
                            <small>{calmNote}</small>
                        </div>
                    </div>
                )}
            />
        )}
        <Link className="daily-link" to={queueHref}>
            Revisar operaciones en curso <DailyIcon name="arrow_forward" />
        </Link>
    </section>
);
